import React, { useState, useEffect } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import Select from "react-select";
import { DURATION_OPTIONS, LEVEL_OPTIONS, PRICING_OPTIONS } from "./courseOptions";
import ImageUpload from "../../common/ImageUpload";

const selectStyles = {
    control: (base, state) => ({
        ...base,
        minHeight: "38px",
        borderColor: state.selectProps.isInvalid ? "#dc3545" : "#dee2e6",
        boxShadow: "none",
        "&:hover": { borderColor: state.selectProps.isInvalid ? "#dc3545" : "#dee2e6" },
    }),
    menu: (base) => ({ ...base, zIndex: 1060 }),
};

export default function CourseModal({ show, onClose, onSubmit, initialData, isLoading }) {
    const [formData, setFormData] = useState({
        title: "",
        pricingTier: "Free",
        link: "",
        imageUrl: "",
        duration: "",
        level: "Beginner",
        sector: "",
        isPopular: false,
        certificate: true,
        institution: "",
        durationUnit: "Hours",
        minPrice: 5,   // Added min price default
        maxPrice: 125, // Added max price default
    });

    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (show) {
            if (initialData) {
                setFormData({
                    title: initialData.title || "",
                    pricingTier: initialData.pricingTier || "Free",
                    link: initialData.link || "",
                    imageUrl: initialData.imageUrl || "",
                    duration: initialData.duration || "",
                    level: initialData.level || "Beginner",
                    sector: initialData.sector || "",
                    isPopular: initialData.isPopular ?? false,
                    certificate: initialData.certificate ?? true,
                    institution: initialData.institution || "",
                    durationUnit: initialData.durationUnit || "Hours",
                    minPrice: initialData.minPrice || 5,   // Populate from initialData if editing
                    maxPrice: initialData.maxPrice || 125, // Populate from initialData if editing
                });
            } else {
                setFormData({
                    title: "",
                    pricingTier: "Free",
                    link: "",
                    imageUrl: "",
                    duration: "",
                    level: "Beginner",
                    sector: "",
                    isPopular: false,
                    certificate: true,
                    institution: "",
                    durationUnit: "Hours",
                    minPrice: 5,   // Reset to 5
                    maxPrice: 125, // Reset to 125
                });
            }
            setErrors({});
        }
    }, [show, initialData]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
        if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
    };

    const handleSelectChange = (field, selectedOption) => {
        setFormData((prev) => ({
            ...prev,
            [field]: selectedOption ? selectedOption.value : "",
        }));
    };

 const validateForm = () => {
        const newErrors = {};
        if (!formData.title.trim()) newErrors.title = "Title is required.";
        if (!formData.institution.trim()) newErrors.institution = "Institution is required.";
        if (!formData.link.trim()) newErrors.link = "Link is required.";
        if (!formData.duration) newErrors.duration = "Duration value is required.";
        if (!formData.sector.trim()) newErrors.sector = "Sector is required.";
        
        if (formData.pricingTier !== "Free") {
            const minP = Number(formData.minPrice);
            const maxP = Number(formData.maxPrice);
            
            if (minP < 5) newErrors.minPrice = "Minimum price cannot be less than $5.";
            if (maxP > 125) newErrors.maxPrice = "Maximum price cannot exceed $125.";
            if (minP > maxP) {
                newErrors.priceRange = "Min price cannot be greater than Max price.";
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };
    
    const handleSubmit = (e) => {
        e.preventDefault();
        if (validateForm()) {
            onSubmit(formData);
        }
    };

    return (
        <Modal show={show} onHide={onClose} centered size="lg">
            <Modal.Header closeButton className="bg-primary text-white">
                <Modal.Title>{initialData ? "Edit Course" : "Add New Course"}</Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit} noValidate>
                <Modal.Body className="p-4">
                    <div className="row g-3">
                        <div className="col-md-6">
                            <Form.Label className="fw-bold">Title <span className="text-danger">*</span></Form.Label>
                            {/* CHANGED TO TEXTAREA HERE */}
                            <Form.Control
                                as="textarea"
                                rows={3}
                                name="title"
                                value={formData.title}
                                onChange={handleInputChange}
                                isInvalid={!!errors.title}
                                placeholder="e.g. Intro to React"
                                style={{ resize: "none" }}
                            />
                            <Form.Control.Feedback type="invalid">{errors.title}</Form.Control.Feedback>
                        </div>

                        <div className="col-md-6">
                            <Form.Label className="fw-bold">Institution <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                type="text"
                                name="institution"
                                value={formData.institution}
                                onChange={handleInputChange}
                                isInvalid={!!errors.institution}
                                placeholder="e.g. Coursera"
                            />
                            <Form.Control.Feedback type="invalid">{errors.institution}</Form.Control.Feedback>
                        </div>

                        <div className="col-md-6">
                            <ImageUpload
                                label="Course Image"
                                value={formData.imageUrl}
                                uploadEndpoint="/upload/pledgeimage"
                                error={errors.imageUrl}
                                onChange={(newUrl) => {
                                    setFormData((prev) => ({ ...prev, imageUrl: newUrl }));
                                    if (errors.imageUrl) setErrors((prev) => ({ ...prev, imageUrl: null }));
                                }}
                            />
                        </div>

                        <div className="col-md-6">
                            <Form.Label className="fw-bold">Course Link <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                type="url"
                                name="link"
                                value={formData.link}
                                onChange={handleInputChange}
                                isInvalid={!!errors.link}
                                placeholder="https://..."
                            />
                            <Form.Control.Feedback type="invalid">{errors.link}</Form.Control.Feedback>
                        </div>

                        <div className="col-md-6">
                            <Form.Label className="fw-bold">Sector <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                type="text"
                                name="sector"
                                value={formData.sector}
                                onChange={handleInputChange}
                                isInvalid={!!errors.sector}
                                placeholder="e.g. IT & Software"
                            />
                            <Form.Control.Feedback type="invalid">{errors.sector}</Form.Control.Feedback>
                        </div>

                        <div className="col-md-6">
                            <div className="row g-2">
                                <div className="col-6">
                                    <Form.Label className="fw-bold">Duration <span className="text-danger">*</span></Form.Label>
                                    <Form.Control
                                        type="number"
                                        name="duration"
                                        value={formData.duration}
                                        onChange={handleInputChange}
                                        isInvalid={!!errors.duration}
                                        min="0"
                                    />
                                    <Form.Control.Feedback type="invalid">{errors.duration}</Form.Control.Feedback>
                                </div>
                                <div className="col-6">
                                    <Form.Label className="fw-bold">Unit</Form.Label>
                                    <Select
                                        options={DURATION_OPTIONS}
                                        value={DURATION_OPTIONS.find((opt) => opt.value === formData.durationUnit)}
                                        onChange={(opt) => handleSelectChange("durationUnit", opt)}
                                        styles={selectStyles}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="col-md-6">
                            <Form.Label className="fw-bold">Level</Form.Label>
                            <Select
                                options={LEVEL_OPTIONS}
                                value={LEVEL_OPTIONS.find((opt) => opt.value === formData.level)}
                                onChange={(opt) => handleSelectChange("level", opt)}
                                styles={selectStyles}
                            />
                        </div>

                        <div className="col-md-6">
                            <Form.Label className="fw-bold">Pricing Tier</Form.Label>
                            <Select
                                options={PRICING_OPTIONS}
                                value={PRICING_OPTIONS.find((opt) => opt.value === formData.pricingTier)}
                                onChange={(opt) => handleSelectChange("pricingTier", opt)}
                                styles={selectStyles}
                            />
                        </div>

                        {formData.pricingTier !== "Free" && (
                            <>
                                <div className="col-md-6">
                                    <Form.Label className="fw-bold">Min Price ($) <span className="text-danger">*</span></Form.Label>
                                    <Form.Control
                                        type="number"
                                        name="minPrice"
                                        value={formData.minPrice}
                                        onChange={handleInputChange}
                                        isInvalid={!!errors.minPrice || !!errors.priceRange}
                                        min="5"
                                        max="125"
                                    />
                                    <Form.Control.Feedback type="invalid">{errors.minPrice}</Form.Control.Feedback>
                                </div>

                                {/* MAX PRICE INPUT */}
                                <div className="col-md-6">
                                    <Form.Label className="fw-bold">Max Price ($) <span className="text-danger">*</span></Form.Label>
                                    <Form.Control
                                        type="number"
                                        name="maxPrice"
                                        value={formData.maxPrice}
                                        onChange={handleInputChange}
                                        isInvalid={!!errors.maxPrice || !!errors.priceRange}
                                        min="5"
                                        max="125"
                                    />
                                    <Form.Control.Feedback type="invalid">{errors.maxPrice || errors.priceRange}</Form.Control.Feedback>
                                </div>
                            </>
                        )}

                        <div className="col-md-12 d-flex gap-4 mt-4">
                            <div className="form-check form-switch">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    role="switch"
                                    id="popular-check"
                                    name="isPopular"
                                    checked={formData.isPopular}
                                    onChange={handleInputChange}
                                    style={{ cursor: "pointer" }}
                                />
                                <label className="form-check-label fw-bold text-black mr-2" htmlFor="popular-check" style={{ cursor: "pointer" }}>
                                    Mark as Popular Course?
                                </label>
                            </div>

                            <div className="form-check form-switch">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    role="switch"
                                    id="certificate-check"
                                    name="certificate"
                                    checked={formData.certificate}
                                    onChange={handleInputChange}
                                    style={{ cursor: "pointer" }}
                                />
                                <label className="form-check-label fw-bold text-black" htmlFor="certificate-check" style={{ cursor: "pointer" }}>
                                    Provides Certificate?
                                </label>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer className="bg-light">
                    <Button variant="secondary" onClick={onClose}>
                        Close
                    </Button>
                    <Button variant="primary" type="submit" disabled={isLoading}>
                        {isLoading ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2"></span>
                                Saving...
                            </>
                        ) : (
                            <>
                                <i className="fas fa-save mr-2"></i>
                                {initialData ? "Update Course" : "Create Course"}
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
}